import { ImageIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  entityAddress,
  entityCoordinates,
  entityLocalized,
  entityName,
  entityNumber,
  entityString,
  formatMoney,
  resolveStorageUrl,
  type ChangeRequest,
  type ModerationEntity,
  type Translate,
} from '@/entities/moderation';
import {
  restaurantCategoryIdsOf,
  useRestaurantCategoryNames,
} from '@/entities/restaurant-category';
import { useVendorName } from '../../../model/use-vendor-name';
import { PreviewableImage } from '../image-preview';
import { RestaurantLocationMap } from '../restaurant-location-map';
import { InfoRow, ReviewSection, ReviewSectionTitle } from '../review-modal-shell';
import { WeekScheduleSection } from '../week-schedule-section';
import { LocalizedRows } from './localized-rows';

/** The restaurant snapshot with its full weekly schedule. */
export function RestaurantSnapshot({
  entity,
  request,
  t,
  verdictInfo,
  weekSchedule,
}: {
  entity: ModerationEntity;
  request: ChangeRequest;
  t: Translate;
  verdictInfo?: ReactNode;
  weekSchedule?: unknown;
}) {
  const vendorName = useVendorName(
    request.merchant_id ?? entityString(entity, 'merchant_id'),
  );
  const categoryIds = restaurantCategoryIdsOf(entity);
  const { names: categoryNames } = useRestaurantCategoryNames(categoryIds);
  const icon = resolveStorageUrl(entityString(entity, 'icon'));
  const background = resolveStorageUrl(entityString(entity, 'background_image'));
  const threshold = entityNumber(entity, 'free_delivery_threshold');
  const minimumOrder = entityNumber(entity, 'min_order_amount');
  const address = entityAddress(entity);
  const phone = entityString(entity, 'phone');
  const noPhoto = t('restaurantCreate.noPhoto', 'Нет фото');

  return (
    <>
      <section className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-col gap-3">
          <ReviewSectionTitle>
            {t('restaurantCreate.restaurantIcon', 'Иконка ресторана')}
          </ReviewSectionTitle>
          {icon === '' ? (
            <EmptyPhoto label={noPhoto} className="size-[124px] rounded-full" />
          ) : (
            <PreviewableImage
              src={icon}
              className="size-[124px] rounded-full border border-border object-cover"
            />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <ReviewSectionTitle>
            {t('restaurantCreate.backgroundPhoto', 'Фото заднего фона')}
          </ReviewSectionTitle>
          {background === '' ? (
            <EmptyPhoto label={noPhoto} className="h-[124px] w-full rounded-[16px]" />
          ) : (
            <PreviewableImage
              src={background}
              className="h-[124px] w-full rounded-[16px] bg-muted object-cover"
            />
          )}
        </div>
      </section>

      <ReviewSection title={t('restaurantCreate.infoSection', 'Основная информация')}>
        <dl className="flex flex-col gap-2.5">
          <InfoRow label={t('create.vendor', 'Мерчант')} value={vendorName} />
          <InfoRow
            label={t('restaurantCreate.categoryName', 'Категории')}
            value={categoryIds.length === 0 ? '—' : categoryNames.join(', ')}
          />
          <InfoRow label={t('table.phone', 'Телефон')} value={phone} numeric />
          <InfoRow
            label={t('restaurantCreate.deliveryAmount', 'Сумма для бесплатной доставки')}
            value={threshold === undefined ? '—' : formatMoney(threshold)}
            numeric
          />
          <InfoRow
            label={t('restaurantCreate.minimumOrder', 'Минимальная сумма заказа')}
            value={minimumOrder === undefined ? '—' : formatMoney(minimumOrder)}
            numeric
          />
          {verdictInfo}
        </dl>
      </ReviewSection>

      <ReviewSection title={t('restaurantCreate.nameSection', 'Наименование ресторана')}>
        <LocalizedRows value={entityName(entity)} t={t} />
      </ReviewSection>

      <ReviewSection
        title={t('restaurantCreate.descriptionSection', 'Описание ресторана')}
      >
        <LocalizedRows value={entityLocalized(entity, 'description')} t={t} stacked />
      </ReviewSection>

      <WeekScheduleSection schedule={weekSchedule ?? entity.week_schedule} t={t} />

      <ReviewSection
        title={t('restaurantCreate.locationSection', 'Локация')}
        divider={false}
      >
        <RestaurantLocationMap
          coords={entityCoordinates(entity)}
          addressLine={address && address.line !== '' ? address.line : '—'}
        />
      </ReviewSection>
    </>
  );
}

function EmptyPhoto({ label, className }: { label: string; className: string }) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground ${className}`}
    >
      <ImageIcon aria-hidden className="size-6" />
      <p className="text-xs leading-[normal] font-normal">{label}</p>
    </div>
  );
}
